import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    MapPin, RefreshCw, Save, X, ChevronDown, ChevronUp, AlertTriangle, Plus,
} from 'lucide-react';
import { GlassCard, GlassButton } from '../ui';
import {
    getGbpCategories,
    saveGbpCategories,
    getGbpCategoryGap,
    refreshGbpCategoryGap,
    GbpOwnCategories,
    GbpCategoryGapReport,
} from '../../services/api';
import './GbpCategoryGapPanel.css';

interface GbpCategoryGapPanelProps {
    projectId: string;
}

const GbpCategoryGapPanel: React.FC<GbpCategoryGapPanelProps> = ({ projectId }) => {
    // -----------------------------------------------------------------------
    // State
    // -----------------------------------------------------------------------
    const [profile, setProfile] = useState<GbpOwnCategories | null>(null);
    const [report, setReport] = useState<GbpCategoryGapReport | null>(null);

    const [loadingProfile, setLoadingProfile] = useState(true);
    const [loadingReport, setLoadingReport] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [saving, setSaving] = useState(false);

    const [profileError, setProfileError] = useState<string | null>(null);
    const [reportError, setReportError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [refreshError, setRefreshError] = useState<string | null>(null);

    // Editable fields
    const [service, setService] = useState('');
    const [city, setCity] = useState('');
    const [chips, setChips] = useState<string[]>([]);
    const [chipInput, setChipInput] = useState('');

    // UI toggles
    const [competitorsExpanded, setCompetitorsExpanded] = useState(false);

    const chipInputRef = useRef<HTMLInputElement>(null);

    // -----------------------------------------------------------------------
    // Load on mount
    // -----------------------------------------------------------------------
    const loadProfile = useCallback(async () => {
        setLoadingProfile(true);
        setProfileError(null);
        try {
            const data = await getGbpCategories(projectId);
            setProfile(data);
            setService(data.service || '');
            setCity(data.city || '');
            setChips(data.own_categories || []);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setProfileError(typeof detail === 'string' ? detail : 'Failed to load your GBP categories.');
        } finally {
            setLoadingProfile(false);
        }
    }, [projectId]);

    const loadReport = useCallback(async () => {
        setLoadingReport(true);
        setReportError(null);
        try {
            const data = await getGbpCategoryGap(projectId);
            setReport(data.report);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setReportError(typeof detail === 'string' ? detail : 'Failed to load the gap report.');
        } finally {
            setLoadingReport(false);
        }
    }, [projectId]);

    useEffect(() => {
        loadProfile();
        loadReport();
    }, [loadProfile, loadReport]);

    // -----------------------------------------------------------------------
    // Chip management
    // -----------------------------------------------------------------------
    const addChip = () => {
        const val = chipInput.trim();
        if (!val) return;
        if (!chips.some(c => c.toLowerCase() === val.toLowerCase())) {
            setChips(prev => [...prev, val]);
        }
        setChipInput('');
        chipInputRef.current?.focus();
    };

    const removeChip = (idx: number) => {
        setChips(prev => prev.filter((_, i) => i !== idx));
    };

    const handleChipKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addChip();
        }
    };

    // -----------------------------------------------------------------------
    // Save own categories
    // -----------------------------------------------------------------------
    const handleSave = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            const data = await saveGbpCategories(projectId, {
                own_categories: chips,
                service: service || undefined,
                city: city || undefined,
            });
            setProfile(data);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setSaveError(typeof detail === 'string' ? detail : 'Failed to save categories.');
        } finally {
            setSaving(false);
        }
    };

    // -----------------------------------------------------------------------
    // Refresh analysis (spends SERP credit)
    // -----------------------------------------------------------------------
    const handleRefresh = async () => {
        setRefreshing(true);
        setRefreshError(null);
        try {
            const data = await refreshGbpCategoryGap(projectId, {
                service: service || undefined,
                city: city || undefined,
            });
            setReport(data.report);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setRefreshError(typeof detail === 'string' ? detail : 'Failed to refresh the gap analysis.');
        } finally {
            setRefreshing(false);
        }
    };

    // -----------------------------------------------------------------------
    // Stale-categories hint
    // Detect mismatch between the current chip list and the snapshot in the report
    // -----------------------------------------------------------------------
    const isStale = Boolean(
        report &&
        !refreshing &&
        JSON.stringify([...chips].sort()) !==
            JSON.stringify([...(report.own_categories || [])].sort()),
    );

    // -----------------------------------------------------------------------
    // Render helpers
    // -----------------------------------------------------------------------
    const loading = loadingProfile || loadingReport;

    if (loading) {
        return (
            <GlassCard className="gbp-panel">
                <div className="lcp-loading">
                    <RefreshCw size={18} className="gbp-spinning" />
                    <span>Loading Local Categories...</span>
                </div>
            </GlassCard>
        );
    }

    if (profileError) {
        return (
            <GlassCard className="gbp-panel">
                <div className="lcp-error">{profileError}</div>
            </GlassCard>
        );
    }

    return (
        <GlassCard className="gbp-panel">
            {/* Header */}
            <div className="lcp-header">
                <div>
                    <h3 className="lcp-title">
                        <MapPin size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                        Local Categories — GBP Gap Analysis
                    </h3>
                    <p className="lcp-subtitle">
                        See which Google Business Profile categories your map-pack competitors use that you don't.
                    </p>
                </div>
            </div>

            {/* ----------------------------------------------------------------
                Inputs: Service + City
            ---------------------------------------------------------------- */}
            <div className="gbp-inputs-section">
                <div className="gbp-input-group">
                    <label className="gbp-input-label">Service / Business type</label>
                    <input
                        style={{ width: '100%', boxSizing: 'border-box', background: 'var(--panel-strong)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', padding: '0.4rem 0.625rem', fontSize: '0.875rem', color: 'var(--text)', outline: 'none' }}
                        placeholder="e.g. plumbing"
                        value={service}
                        onChange={e => setService(e.target.value)}
                    />
                </div>
                <div className="gbp-input-group">
                    <label className="gbp-input-label">City / Location</label>
                    <input
                        style={{ width: '100%', boxSizing: 'border-box', background: 'var(--panel-strong)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', padding: '0.4rem 0.625rem', fontSize: '0.875rem', color: 'var(--text)', outline: 'none' }}
                        placeholder="e.g. Melbourne"
                        value={city}
                        onChange={e => setCity(e.target.value)}
                    />
                </div>
            </div>

            {/* ----------------------------------------------------------------
                Your GBP categories
            ---------------------------------------------------------------- */}
            <div className="gbp-section-title">
                <MapPin size={14} />
                Your Google Business Profile categories
            </div>

            <div className="gbp-chip-area">
                {chips.map((chip, idx) => (
                    <span key={idx} className="gbp-category-chip">
                        {chip}
                        <button onClick={() => removeChip(idx)} title="Remove">
                            <X size={12} />
                        </button>
                    </span>
                ))}
            </div>

            <div className="gbp-chip-input-row">
                <input
                    ref={chipInputRef}
                    placeholder="Type a category and press Enter or Add"
                    value={chipInput}
                    onChange={e => setChipInput(e.target.value)}
                    onKeyDown={handleChipKeyDown}
                />
                <GlassButton variant="secondary" onClick={addChip} disabled={!chipInput.trim()}>
                    <Plus size={14} />
                    Add
                </GlassButton>
            </div>

            {chips.length === 0 && (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                    No categories saved yet — every competitor category will appear as "missing" until you add your own.
                </p>
            )}

            {/* Stale-categories hint */}
            {isStale && (
                <div className="gbp-stale-hint">
                    <AlertTriangle size={14} />
                    Your categories have changed since the last analysis — refresh to update the gap.
                </div>
            )}

            {saveError && <div className="lcp-error" style={{ marginTop: '0.5rem' }}>{saveError}</div>}

            {/* ----------------------------------------------------------------
                Action row
            ---------------------------------------------------------------- */}
            <div className="gbp-action-row">
                <GlassButton variant="primary" onClick={handleSave} disabled={saving}>
                    {saving ? <RefreshCw size={14} className="gbp-spinning" /> : <Save size={14} />}
                    {saving ? 'Saving…' : 'Save categories'}
                </GlassButton>

                <GlassButton variant="primary" onClick={handleRefresh} disabled={refreshing}>
                    <RefreshCw size={14} className={refreshing ? 'gbp-spinning' : ''} />
                    {refreshing ? 'Fetching map-pack…' : 'Refresh analysis'}
                </GlassButton>

                <span className="gbp-cost-note">
                    Refresh fetches live Google map-pack results — uses your SERP quota.
                </span>
            </div>

            {refreshError && <div className="lcp-error" style={{ marginTop: '0.5rem' }}>{refreshError}</div>}

            {/* ----------------------------------------------------------------
                Report section
            ---------------------------------------------------------------- */}
            {!report && !reportError && (
                <div className="gbp-empty-prompt">
                    <MapPin size={36} />
                    <p>No analysis yet. Enter your service and city, then click <strong>Refresh analysis</strong> to fetch live map-pack data.</p>
                </div>
            )}

            {reportError && (
                <div className="lcp-error" style={{ marginTop: '1rem' }}>{reportError}</div>
            )}

            {report && (
                <>
                    {/* Low confidence banner */}
                    {report.low_confidence && (
                        <div className="gbp-warn-banner">
                            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                            <span>
                                {report.competitors && report.competitors.length > 0
                                    ? <>Local listings were found for "<strong>{report.query || 'this query'}</strong>" but none exposed GBP categories — try a different city or service to get category data.</>
                                    : <>No local listings were returned for "<strong>{report.query || 'this query'}</strong>" — try a broader service or different city.</>
                                }
                                {report.refreshed_at && (
                                    <> Last refreshed: {new Date(report.refreshed_at).toLocaleString()}.</>
                                )}
                            </span>
                        </div>
                    )}

                    {/* Missing categories table */}
                    {report.missing_categories.length > 0 && (
                        <div style={{ marginTop: '1.5rem' }}>
                            <div className="gbp-section-title">
                                Missing categories ({report.missing_categories.length})
                            </div>
                            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                                Competitors in your map pack use these categories — add the ones that fit your business.
                            </p>
                            <table className="seo-keyword-table gbp-table">
                                <thead>
                                    <tr>
                                        <th>Category</th>
                                        <th style={{ width: 60, textAlign: 'center' }}>Competitors</th>
                                        <th>Used by</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.missing_categories.map((mc, idx) => (
                                        <tr key={idx}>
                                            <td>
                                                <span className="gbp-category-chip" style={{ cursor: 'default' }}>
                                                    {mc.category}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                                                {mc.used_by_count}
                                            </td>
                                            <td>
                                                <div className="gbp-used-by-chips">
                                                    {mc.used_by.map((name, ni) => (
                                                        <span key={ni} className="gbp-competitor-name-chip">{name}</span>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {!report.low_confidence && report.missing_categories.length === 0 && report.competitors.length > 0 && (
                        <div className="gbp-empty-prompt" style={{ paddingTop: '1.5rem' }}>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                You already share all competitor categories. No gaps found.
                            </p>
                        </div>
                    )}

                    {/* Shared categories summary */}
                    {report.shared_categories.length > 0 && (
                        <div style={{ marginTop: '1rem' }}>
                            <div className="gbp-section-title" style={{ fontSize: '0.8rem' }}>
                                Categories you share with competitors ({report.shared_categories.length})
                            </div>
                            <div className="gbp-categories-in-row">
                                {report.shared_categories.map((cat, idx) => (
                                    <span key={idx} className="gbp-category-chip" style={{ cursor: 'default', opacity: 0.75 }}>
                                        {cat}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Competitors collapsible section */}
                    {report.competitors.length > 0 && (
                        <>
                            <button
                                className="gbp-competitors-toggle"
                                onClick={() => setCompetitorsExpanded(prev => !prev)}
                            >
                                {competitorsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                Map-pack competitors ({report.competitors.length})
                            </button>

                            {competitorsExpanded && (
                                <table className="seo-keyword-table gbp-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 40 }}>#</th>
                                            <th>Business</th>
                                            <th style={{ width: 60, textAlign: 'center' }}>Rating</th>
                                            <th>Categories</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.competitors.map((comp, idx) => (
                                            <tr key={idx}>
                                                <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                                                    {comp.rank}
                                                </td>
                                                <td>
                                                    {comp.url ? (
                                                        <a
                                                            href={comp.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="gcg-page-link"
                                                        >
                                                            {comp.name}
                                                        </a>
                                                    ) : (
                                                        <span style={{ fontSize: '0.85rem' }}>{comp.name}</span>
                                                    )}
                                                    {comp.reviews_count != null && (
                                                        <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginLeft: 6 }}>
                                                            {comp.reviews_count} reviews
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                                                    {comp.rating != null ? comp.rating.toFixed(1) : '—'}
                                                </td>
                                                <td>
                                                    <div className="gbp-categories-in-row">
                                                        {comp.categories.length > 0
                                                            ? comp.categories.map((cat, ci) => (
                                                                <span key={ci} className="gbp-category-chip" style={{ cursor: 'default', fontSize: '0.7rem' }}>
                                                                    {cat}
                                                                </span>
                                                            ))
                                                            : <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>No categories</span>
                                                        }
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </>
                    )}

                    {/* Footer meta */}
                    {report.refreshed_at && (
                        <p style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '1rem', textAlign: 'right' }}>
                            Analysis via {report.provider} · {new Date(report.refreshed_at).toLocaleString()}
                        </p>
                    )}
                </>
            )}
        </GlassCard>
    );
};

export default GbpCategoryGapPanel;
